import { ThunkAction } from 'redux-thunk';
import { VoucherBasketItemDm } from 'weborder/Api';

import * as Basket from '../BasketControl';
import { basketLogger } from '../logger';
import { basketActions, basketSelectors } from '../redux';
import { OrderPayload } from '../selectors/basketSelectors';
import { orderApiServices } from '../services';
import * as Store from '../Store';
import { tokenize } from '../utils';
import { AddToast } from './Actions';

type ThunkResult<R> = ThunkAction<R, Store.State, undefined, Store.Action>;

// #region BASKET

// TEMPORARY
export const removeFromBasket =
  (payload: { menuId: number; index: number }): ThunkResult<void> =>
  (dispatch, getState) => {
    const { index } = payload;
    const state = getState();
    const oldBasket = basketSelectors.getBasketBridge(state);
    const item = oldBasket.items[index];

    dispatch({
      type: 'REMOVE_FROM_BASKET',
      payload: item,
    });
  };

export type ClearBasketProps = {
  menuId: number;
};

export const clearBasket =
  (payload: ClearBasketProps): ThunkResult<void> =>
  (dispatch) => {
    if (!payload.menuId) return;

    const nextBasket: Store.ClearBasket = {
      type: 'CLEAR_BASKET',
      payload: {
        ...Basket.Empty,
        menuId: payload.menuId,
      },
    };

    dispatch(nextBasket);
  };
// #endregion

// #region VOUCHER
export type VoucherProps = {
  orderPayload: OrderPayload;
  code?: string;
  throwError?: boolean;
};

const setVoucherState =
  (payload: Store.SetVoucher['payload']): ThunkResult<void> =>
  (dispatch) => {
    dispatch({
      type: 'SET_VOUCHER',
      payload,
    });
  };

export const checkAutoApplyVoucher =
  (orderPayload: OrderPayload): ThunkResult<void> =>
  async (dispatch) => {
    if (!orderPayload || typeof orderPayload === 'string') {
      return;
    }

    try {
      basketLogger.debug('VOUCHER CHECK_AUTO_APPLY ...');

      dispatch(
        setVoucherState({
          menuId: orderPayload.MenuId,
          voucher: { status: 'loading', code: ' ', items: [] },
        }),
      );

      const order = await orderApiServices.create(orderPayload);

      if (!order) {
        throw new Error('no order found');
      }
      // store created order in basket.orders so we can reuse
      const uniqueKey = tokenize(orderPayload);
      dispatch(
        basketActions.keepCreatedOrder({ [uniqueKey]: { ...order } }) as any,
      );

      if (
        order &&
        order.OrderId &&
        order.VoucherBasketItems &&
        order.VoucherBasketItems[0]
      ) {
        const voucher = order.VoucherBasketItems[0];
        dispatch(
          setVoucherState({
            menuId: orderPayload.MenuId,
            voucher: {
              status: 'valid',
              code: voucher.Name,
              items: order.VoucherBasketItems,
            },
          }),
        );
      } else {
        dispatch(
          setVoucherState({
            menuId: orderPayload.MenuId,
            voucher: {
              status: 'none',
              code: '',
            },
          }),
        );
      }

      basketLogger.debug('VOUCHER CHECK_AUTO_APPLY DONE');
    } catch (error) {
      basketLogger.debug('VOUCHER CHECK_AUTO_APPLY ERROR', error);
      dispatch(
        setVoucherState({
          menuId: orderPayload.MenuId,
          voucher: { status: 'none', code: ' ' },
        }),
      );

      dispatch(AddToast(error.message));
    }
  };

export const checkUserVoucher =
  (props: VoucherProps): ThunkResult<void> =>
  async (dispatch, getState): Promise<VoucherBasketItemDm | null> => {
    const { orderPayload, code } = props;
    if (!orderPayload || typeof orderPayload === 'string') {
      return null;
    }

    const state = getState();
    const basket = basketSelectors.getBasketBridge(state);
    const { voucher } = basket;

    try {
      basketLogger.debug('VOUCHER CHECK_USER ...');

      dispatch(
        setVoucherState({
          menuId: orderPayload.MenuId,
          voucher: {
            status: 'loading',
            code: code || voucher.code,
            items: [],
            isUserInput: true,
          },
        }),
      );

      // don't send PaymentAccountId coz backend will auto-apply voucher
      const orderPayloadNoPayment = {
        ...orderPayload,
        PaymentAccountId: undefined,
      };
      const order = await orderApiServices.create(orderPayloadNoPayment);
      if (!order) {
        throw new Error('no order found');
      }
      const nextVoucher = await orderApiServices.applyVoucher({
        orderId: order.OrderId,
        voucherCode: code || voucher.code,
        paymentAccountId: orderPayload.PaymentAccountId,
      });

      // store created order in basket.orders so we can reuse
      const uniqueKey = tokenize(orderPayload);
      dispatch(
        // @ts-ignore voucher must be there
        basketActions.keepCreatedOrder({
          [uniqueKey]: { ...order, VoucherBasketItems: nextVoucher },
        }) as any,
      );

      if (nextVoucher && nextVoucher[0]) {
        const newVoucher = nextVoucher[0];
        dispatch(
          setVoucherState({
            menuId: orderPayload.MenuId,
            voucher: {
              status: 'valid',
              code: newVoucher.Name,
              items: nextVoucher,
              isUserInput: true,
            },
          }),
        );
        return newVoucher;
      }
      dispatch(
        setVoucherState({
          menuId: orderPayload.MenuId,
          voucher: {
            status: 'none',
            code: ' ',
          },
        }),
      );
      basketLogger.debug('VOUCHER CHECK_USER DONE');
      return null;
    } catch (error) {
      basketLogger.debug('VOUCHER CHECK_USER ERROR', error);
      dispatch(
        setVoucherState({
          menuId: orderPayload.MenuId,
          // fixes https://flipdish.atlassian.net/browse/GTS-1402 but after backend fixes error UserMessage
          // voucher: { status: 'none', code: '' }
          voucher:
            voucher && voucher.status === 'valid'
              ? voucher
              : { status: 'none', code: ' ' },
        }),
      );

      if (props.throwError) {
        throw error;
      }
      return null;
    }
  };

export const checkVoucher =
  (props: VoucherProps): ThunkResult<void> =>
  async (dispatch, getState) => {
    const { orderPayload, code } = props;

    if (!orderPayload || typeof orderPayload === 'string') {
      return;
    }

    const state = getState();
    const basket = basketSelectors.getBasketBridge(state);
    const { voucher } = basket;

    // KNOWN BUG we should cancel current or something
    if (voucher.status === 'loading') {
      basketLogger.debug('VOUCHER CHECKING ALREADY_IN_PROGRESS');
      return;
    }

    basketLogger.debug('VOUCHER CHECKING...');

    const isInitial = voucher.status === 'none' && voucher.code === '';

    const isAutoApply =
      voucher.status === 'valid' && (!voucher.isUserInput || !!code);

    const canApplyVoucher = isInitial || isAutoApply;

    if (canApplyVoucher) {
      dispatch(checkAutoApplyVoucher(orderPayload));
      return;
    }

    const isUserInput =
      (voucher.status === 'valid' && voucher.isUserInput) || !!code;

    if (isUserInput) {
      dispatch(checkUserVoucher({ orderPayload, code }));
      return;
    }

    basketLogger.debug('VOUCHER CHECKING END');
  };
// #endregion
